import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Container, Typography } from "src/UILibrary"
import { useRecoilState } from "recoil"
import Stepper from "src/components/atom/Stepper"
import { AdviserGeneralInformation } from "./layout/GeneralInformation"
import { AdviserPRInformation } from "./layout/PRInformation"
import { GENDER, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import dayjs, { Dayjs } from "dayjs"
import { AddressService } from "src/openapi_address"
import { validateGeneralInformation, validatePRInformation } from "./validation"
import { ShowToast } from "src/states/showToast"
import { useMediaQuery } from "react-responsive"
import { Career, ProfileService } from "src/openapi"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ShowProgress } from "src/states/showProgress"

const STEP = {
  GENERAL_INFORMATION: 1,
  PR_INFORMATION: 2,
}

export const AdviserProfilePage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [step, setStep] = useState<number>(STEP.GENERAL_INFORMATION)

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [jobs, setJobs] = useState<number[]>([])
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [gender, setGender] = useState<string>(GENDER.MALE)
  const [nation, setNation] = useState<string>("Japan")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [areaId, setAreaId] = useState<number>(1)
  const [avatarPath, setAvatarPath] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [province, setProvince] = useState<string>("北海道")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [birthdayValidationMessage, setBirthdayValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [avatarPathValidationMessage, setAvatarPathValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")

  const [abstract, setAbstract] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [career, setCareer] = useState<Career[]>([
    { year: new Date().getFullYear(), month: new Date().getMonth() + 1, career: "" } as Career,
  ])
  const [abstractValidationMessage, setAbstractValidationMessage] = useState<string>("")
  const [messageValidationMessage, setMessageValidationMessage] = useState<string>("")
  const [careerValidationMessage, setCareerValidationMessage] = useState<string>("")

  const { accessToken, checkLoginStatus, initialized, awsID } = useLoginContext()
  useEffect(() => {
    if (!initialized) {
      return
    }
    handleGetProfile()
  }, [awsID, initialized])

  useEffect(() => {
    const section = document.querySelector("#box_profile")
    section!.scrollTop = 0
  }, [step])

  const handleGetProfile = () => {
    ProfileService.adviserDetail(awsID)
      .then((res) => {
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setFirstName(res.data?.firstName!)
          setLastName(res.data?.lastName!)
          setFirstNameJP(res.data?.firstNameKana!)
          setLastNameJP(res.data?.lastNameKana!)
          let jobs: number[] = []
          res.data?.jobTitleId?.split(",").map((id) => jobs.push(parseInt(id)))
          setJobs(jobs)
          setBirthday(dayjs(res.data?.birthday!))
          setGender(res.data?.gender!)
          setNation(res.data?.country!)
          setPhoneNumber(res.data?.phoneNumber!)
          setAreaId(res.data?.areaId!)
          setAvatarPath(res.data?.photoUrl!)
          setPostCode(res.data?.zipCode!)
          setProvince(res.data?.province!)
          setCity(res.data?.city!)
          setStreet(res.data?.streetNo!)
          setBuilding(res.data?.building!)

          setAbstract(res.data?.pr!)
          setMessage(res.data?.message!)
          setCareer(res.data?.career as Career[])
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handlePostCodeInput = () => {
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode).then((res) => {
      if (res.prefecture) {
        setProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setCity(city)
      }
    })
  }

  const confirmGeneralInformation = async () => {
    if (
      !(await validateGeneralInformation(
        firstName,
        lastName,
        firstNameJP,
        lastNameJP,
        birthday ? birthday?.format("YYYY-MM-DD") : "",
        phoneNumber,
        avatarPath,
        postCode,
        city,
        setNameValidationMessage,
        setBirthdayValidationMessage,
        setPhoneNumberValidationMessage,
        setAvatarPathValidationMessage,
        setPostCodeValidationMessage,
        setCityValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.PR_INFORMATION)
  }

  const handleRegister = async () => {
    if (
      !(await validatePRInformation(
        abstract,
        setAbstractValidationMessage,
        message,
        setMessageValidationMessage,
        career,
        setCareerValidationMessage,
        t
      ))
    ) {
      return
    }

    setShowProgress(true)
    ProfileService.updateAdviser(accessToken, awsID, {
      photoUrl: avatarPath,
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      jobTitleId: jobs.join(","),
      gender: gender,
      birthday: birthday?.format("YYYY-MM-DD") as string,
      country: nation,
      areaId: areaId,
      phoneNumber: phoneNumber,
      zipCode: postCode,
      province: province,
      city: city,
      streetNo: street,
      building: building,
      pr: abstract,
      message: message,
      career: career,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <>
      <Container maxWidth="xl" sx={{ m: "0px", ...(isTablet ? {} : { p: "0px!important" }) }}>
        {isTablet ? (
          <Typography.Large
            color="textPrimary"
            sx={{
              my: "0px",
            }}
          >
            {t("update_adviser_profile", { step: step })}
          </Typography.Large>
        ) : (
          <Typography.SubHeadNormal
            color="textPrimary"
            sx={{
              mt: "20px",
            }}
          >
            {t("update_adviser_profile", { step: step })}
          </Typography.SubHeadNormal>
        )}

        <Stepper
          steps={[t("adviser_step_1"), t("adviser_step_2")]}
          current={step}
          sx={{ mb: isTablet ? "60px" : "30px" }}
        ></Stepper>
        {step === STEP.GENERAL_INFORMATION && (
          <AdviserGeneralInformation
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            jobValues={jobs}
            handleJobs={setJobs}
            birthday={birthday}
            handleBirthday={setBirthday}
            gender={gender}
            handleGener={setGender}
            nation={nation}
            handleNation={setNation}
            areaId={areaId}
            handleAreaId={setAreaId}
            avatar={avatarPath}
            handleAvatar={setAvatarPath}
            phoneNumber={phoneNumber}
            handlePhoneNumber={setPhoneNumber}
            postCode={postCode}
            handlePostCode={setPostCode}
            handlePostCodeInput={handlePostCodeInput}
            province={province}
            handleProvince={setProvince}
            city={city}
            handleCity={setCity}
            street={street}
            handleStreet={setStreet}
            building={building}
            handleBuilding={setBuilding}
            nameValidationMessage={nameValidationMessage}
            birthdayValidationMessage={birthdayValidationMessage}
            phoneNumberValidationMessage={phoneNumberValidationMessage}
            avatarPathValidationMessage={avatarPathValidationMessage}
            postCodeValidationMessage={postCodeValidationMessage}
            cityValidationMessage={cityValidationMessage}
            handleNext={() => confirmGeneralInformation()}
          />
        )}
        {step === STEP.PR_INFORMATION && (
          <AdviserPRInformation
            abstract={abstract}
            handleAbstract={setAbstract}
            message={message}
            handleMessage={setMessage}
            career={career}
            handleCareer={setCareer}
            abstractValidationMessage={abstractValidationMessage}
            messageValidationMessage={messageValidationMessage}
            careerValidationMessage={careerValidationMessage}
            handlePrev={() => setStep(STEP.GENERAL_INFORMATION)}
            handleNext={() => handleRegister()}
          />
        )}
      </Container>
    </>
  )
}
